import { useTranslation } from "react-i18next";
import GenericPromises from "../../../../../api/GenericPromises";
import useSnackBar from "../../../../../components/Commons/SnackBar/useSnackBar";
import { FormsErrors } from "../../../../../hooks/Forms/FormsErrors";
import { Dispatch, RefObject, SetStateAction, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Backdrop, Box, CircularProgress, TextField, Typography } from "@mui/material";
import {
    ButtonStyled,
    PrimaryButton,
} from "../../../../../theme/buttons";
import { Notification } from "../../../../../interfaces/Commons/Notifications/notifications";
import { GridValidRowModel } from "@mui/x-data-grid";
import { User } from "../../../../../interfaces/Security/users";
import { useParamsFilter } from "../../../../../hooks/useParamsFilter";
import { NotificationDetail } from "../../../../../interfaces/Commons/Notifications/notificationDetails";
import { PaymentDetail } from "../../../../../interfaces/Purchases/Payments/payments";
import { FilterTableRef } from "../../../../../components/Filter";

type AddNotificationNoAuthorizePaymentsProps = {
    setOpenDialogAddNotification: Dispatch<SetStateAction<boolean>>;
    selectedRows: readonly GridValidRowModel[];
    setTotalPaymentsDetailsData: Dispatch<SetStateAction<readonly GridValidRowModel[]>>;
    refTableFilter: RefObject<FilterTableRef>;
};

export const AddNotificationNoAuthorizePayments = ({
    setOpenDialogAddNotification,
    selectedRows,
    setTotalPaymentsDetailsData,
    refTableFilter,
}: AddNotificationNoAuthorizePaymentsProps) => {
    const [t] = useTranslation("global");
    const { GenericPostResource, GenericPutResource } = GenericPromises();
    const { showSnackBar, SnackbarComponent } = useSnackBar();
    const { GetError } = FormsErrors();
    const [loadingPost, setLoadingPost] = useState(false);
    const [isBackdrop, setIsBackdrop] = useState(false);
    const { ConvertParamsToBase64 } = useParamsFilter();

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<Notification>({
        defaultValues: {
            notification_type_id: undefined,
            notification_status_id: undefined,
            user_id: undefined,
            notification_name: t("purchaseinvoices.notifications.messages.unauthorized-payment"),
            notification_description: undefined,
            read_at: undefined,
            delete_at: undefined,
        },
    });

    const onSubmit: SubmitHandler<Notification> = (data) => onPost(data);
    const onPost = async (data: Notification) => {
        setIsBackdrop(true);


        // update is_authorized false
        for (let i = 0; i < selectedRows.length; i++) {
            const e: PaymentDetail = selectedRows[i];
            if (!e.payment_id) {
                let myUpdateObj = {
                    is_authorized: false,
                    authorization_date: null,
                    authorization_user: null,
                }
                // Update intern selectedRows o la data de la tabla
                await GenericPutResource(`/authorizationpayments/${e.payment_detail_id}`, myUpdateObj)
                    .then(() => {
                        refTableFilter.current?.applyFilter();
                        setTotalPaymentsDetailsData(prevData =>
                            prevData.map((item: any) =>
                                (e.payment_detail_id === item.payment_detail_id)
                                    ? { ...item, is_authorized: false, authorization_date: null, authorization_user: null, }
                                    : item
                            )
                        );
                    });
            }
        }


        const paymentIds = selectedRows.map((e: PaymentDetail) => e.purchase_invoice_id).join(",");

        let objectData = {
            payment_ids: paymentIds,
            notification_description: data.notification_description,
        }


        GenericPostResource(`/notifications/createunauthorizedpaymentnotifications`, objectData)
            .then(() => {
                showSnackBar(t("purchaseinvoices.notifications.messages.all-notifications-sent"), "success");
            })
            .catch(async (error) => {
                showSnackBar(error.message, "error");
            })
            .finally(() => {
                setOpenDialogAddNotification(false);
                setIsBackdrop(false);
                setLoadingPost(false);
            })
    };



    const onCancel = () => {
        setOpenDialogAddNotification(false);
    };


    return (
        <>
            <Box className="d-flex justify-content-center" sx={{ minWidth: 1000 }}>
                <form onSubmit={handleSubmit(onSubmit)} className="w-100">
                    <Typography variant="body1" >
                        {t("purchaseinvoices.notifications.messages.notification_warning")}
                    </Typography>
                    <div className="d-flex mt-1 pt-3">
                        <div className="w-100">
                            <TextField
                                variant="filled"
                                label={`${t("notifications.fields.notification_name")}`}
                                value={`${t("purchaseinvoices.notifications.messages.unauthorized-payment")}`}
                                size="small"
                                sx={{ width: "100%", }}
                                InputProps={{ readOnly: true }}
                            />
                        </div>
                    </div>
                    <div className="d-flex mt-1 pt-3">
                        <div className="w-100">
                            <Controller
                                name="notification_description"
                                control={control}
                                rules={{ required: true, maxLength: 300 }}
                                render={({ field }) => (
                                    <TextField
                                        variant="filled"
                                        label={`${t(
                                            "notifications.fields.notification_description"
                                        )}`}
                                        ref={field.ref}
                                        value={field.value}
                                        multiline
                                        minRows={5}
                                        onChange={(event) => {
                                            field.onChange(event.target.value);
                                        }}
                                        error={!!errors.notification_description}
                                        helperText={GetError(
                                            errors.notification_description?.type
                                        )}
                                        size="small"
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="d-flex flex-row-reverse mt-3">
                        <PrimaryButton
                            type="submit"
                            variant="outlined"
                            className="m-1"
                            disabled={loadingPost}
                        >
                            {t("generic.buttons.accept")}
                        </PrimaryButton>
                        <ButtonStyled
                            variant="contained"
                            className="m-1"
                            onClick={onCancel}
                        >
                            {t("generic.buttons.cancel")}
                        </ButtonStyled>
                    </div>
                </form>
            </Box >
            <SnackbarComponent />
            <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={isBackdrop}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
};
